import React from "react";
import { Link } from "react-router-dom";

export function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms of Service</h1>
        <p className="text-slate-500 mb-8">Last updated: December 18, 2025</p>

        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              By accessing or using Calyro's AI voice agent services ("Services"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use our Services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">2. Description of Services</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Calyro provides AI-powered voice agents that answer phone calls, capture leads, and schedule appointments for businesses. Our Services include:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>24/7 AI call answering</li>
              <li>Lead capture and qualification</li>
              <li>Appointment scheduling</li>
              <li>Call recordings and summaries</li>
              <li>Dashboard and analytics</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">3. Pricing and Payment</h2>
            <div className="text-slate-600 leading-relaxed space-y-4">
              <p><strong>Setup Fee:</strong> A mandatory one-time setup fee is required before your AI agent goes live. This fee covers custom configuration, voice tuning, and quality assurance testing.</p>
              <p><strong>Subscription:</strong> After setup, you will be billed monthly or yearly based on your selected plan. Plans include a set number of minutes per month.</p>
              <p><strong>Overages:</strong> Usage beyond your included minutes is billed at your plan's overage rate per minute.</p>
              <p><strong>Hard Caps:</strong> Each plan has a hard cap on minutes. If you reach your hard cap, calls will be routed to voicemail until you upgrade or your billing cycle resets.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">4. Billing and Cancellation</h2>
            <div className="text-slate-600 leading-relaxed space-y-4">
              <p><strong>Billing Cycle:</strong> Subscriptions are billed in advance on a monthly or yearly basis.</p>
              <p><strong>Cancellation:</strong> You may cancel your subscription at any time. Access continues until the end of your current billing period. No prorated refunds for monthly plans. Yearly plans may be eligible for prorated refunds within 30 days of purchase.</p>
              <p><strong>Downgrades:</strong> Plan downgrades take effect at the start of your next billing cycle.</p>
              <p><strong>Upgrades:</strong> Plan upgrades are effective immediately with prorated charges.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">5. Acceptable Use</h2>
            <p className="text-slate-600 leading-relaxed mb-4">You agree not to use our Services to:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              <li>Make fraudulent, misleading, or deceptive calls</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Harass, abuse, or harm others</li>
              <li>Interfere with or disrupt our Services</li>
              <li>Attempt to gain unauthorized access to our systems</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">6. Service Level</h2>
            <div className="text-slate-600 leading-relaxed space-y-4">
              <p><strong>Availability:</strong> We strive for 99.9% uptime but do not guarantee uninterrupted service.</p>
              <p><strong>Setup Timeline:</strong> Most clients are live within 24-48 hours of setup fee payment and completing onboarding information.</p>
              <p><strong>Support:</strong> Support response times vary by plan. See your plan details for specific SLA.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">7. Data and Privacy</h2>
            <p className="text-slate-600 leading-relaxed">
              Your use of our Services is also governed by our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>. Call recordings and data are stored securely and retained according to your preferences and applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-slate-600 leading-relaxed">
              To the maximum extent permitted by law, Calyro shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including lost profits, revenue, or data, arising from your use of our Services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">9. Changes to Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update these Terms from time to time. We will notify you of material changes via email or through our Services. Continued use after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">10. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have questions about these Terms, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-slate-700 font-medium">Calyro, Inc.</p>
              <p className="text-slate-600">Email: support@calyro.ai</p>
              <p className="text-slate-600">Address: 123 Business Ave, Suite 100, San Francisco, CA 94102</p>
            </div>
          </section>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-blue-600 hover:underline">← Back to Home</Link>
        </div>
      </div>
    </div>
  );
}
